import {Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {first} from 'rxjs/operators';
import {LocalModalComponent} from '~shared/components/elements/local-modal/local-modal.component';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {isEmailValid, isStreetValid, noWhitespaceCharacters} from '~shared/directives/form-validators';
import {NotificationType} from '~core/enum';
import {Address, AuthenticatedUser, Club, Country, Customer, CustomerType, GooglePlacesAddress} from '~core/models';
import {
  AddressService,
  CustomerService,
  DefaultNotificationService,
  StorageService,
} from '~core/services';
import {CreateCustomerTranslationsService} from '~shared/translations';
import {isPastDateDirective} from '~shared/directives/form-validators/is-past-date.directive';
import {LeaveConfirmationComponent} from "~features/create-customer/components/leave-confirmation/leave-confirmation.component";
import {DuplicateCheckService} from "~features/create-customer/duplicate-check.service";


/**
 * Creates a new customer
 */
@UntilDestroy()
@Component({
  templateUrl: 'create-customer.component.html',
  styleUrls: ['create-customer.component.scss']
})
export class CreateCustomerComponent implements OnInit, OnDestroy {
  public countries: Array<Country> = [];
  public customer: Customer;
  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;
  public customerFormPrivate: UntypedFormGroup;
  public customerFormCompany: UntypedFormGroup;
  public communicationForm: UntypedFormGroup;
  public fullCustomerForm: UntypedFormGroup;
  public addressesForm: UntypedFormGroup;
  public submitted: boolean = false;
  public stepProcess: number = 1;
  public isPrivateCustomer: boolean = true;
  public duplicateCheckInProgress: boolean = false;
  public isEmailValid: boolean = true;
  public listOfDuplicates: any;
  public searchHistory: any;
  public spinnerName: string = 'submitCustomer';
  public existingCustomer: any = $localize`:@@createCustomerComponent.modalHeader.existingCutomer:Existing customer`;
  public useExisting: any = $localize`:@@createCustomerComponent.button.useExisting:Use Existing`;

  @ViewChild(LeaveConfirmationComponent, {static: true}) public child: LeaveConfirmationComponent;
  @ViewChild(LocalModalComponent) public selectedCustomerModal: LocalModalComponent;

  constructor(
    private userService: CustomerService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private addressService: AddressService,
    private createCustomerTranslation: CreateCustomerTranslationsService,
    private storage: StorageService,
    private spinnerService: SpinnerService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public duplicateCheckService: DuplicateCheckService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    if (this.storage.checkIfNotEmpty(`${this.locale}-countries`)) {
      this.storage.castCurrentCountries.pipe(untilDestroyed(this))
        .subscribe(countries => this.countries = countries);
    }
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((club: Club) => this.selectedClub = club);
    this.storage.castCustomerSearchHistory.pipe(untilDestroyed(this))
      .subscribe(searchHistory => this.searchHistory = searchHistory);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
  }

  /**
   * Creates the form on component init
   */
  public ngOnInit(): any {
    this.fillCustomerFormPrivate();
    this.fillCustomerFormCompany();
    this.fillCommunicationForm();
    this.fillAddressForm();
  }

  /**
   * Removes modal status when leaving page
   */
  public ngOnDestroy(): any {
    this.selectedCustomerModal.setModalStatus(false);
  }

  /**
   * Creating a set of data for the customer
   */
  public createNewCustomer(): void {
    let customerForm;
    if (this.isPrivateCustomer) {
      customerForm = this.customerFormPrivate;
    } else {
      customerForm = this.customerFormCompany;
    }
    if (customerForm.valid) {
      this.goForwardAStep();
    }
  }

  /**
   * Creating a set of data for communication.
   */
  public createNewCommunication(): void {
    if (this.communicationForm.valid) {
      this.goForwardAStep();
    }
  }

  /**
   * Creating a set of data for Address.
   */
  public createNewAddress(): void {
    const required = ['city', 'country', 'postalCode', 'street'];
    for (const key of Object.keys(this.addressesForm.controls)) {
      if (required.includes(key) && (this.addressesForm.get(key).value !== null &&
        (this.addressesForm.get(key).value.length > 0 && this.addressesForm.get(key).value !== 'undefined'))) {
      }
    }

    if (this.addressesForm.valid) {
      this.submitCustomer();
    }
  }

  /**
   * Creating a new customer by sending a post request to the customer API
   */
  public submitCustomer(): any {
    this.spinnerService.show(this.spinnerName);
    if (this.isPrivateCustomer) {
      this.fillFullCustomerForm(this.customerFormPrivate);
    } else {
      this.fillFullCustomerForm(this.customerFormCompany);
    }

    let duplicateCheckQuery = '';
    Object.keys(this.fullCustomerForm.controls).forEach(key => {
      const required = ['firstName', 'lastName', 'phone', 'mobile', 'birthday', 'email'];
      if (required.includes(key) && this.fullCustomerForm.get(key).value != null) {
        if (duplicateCheckQuery !== '') {
          duplicateCheckQuery += `&${key}=${this.fullCustomerForm.get(key).value}`;
        } else {
          duplicateCheckQuery += `${key}=${this.fullCustomerForm.get(key).value}`;
        }
      }
    });
    Object.keys(this.addressesForm.controls).forEach(key => {
      const required = ['postalCode', 'street'];
      if (required.includes(key)
        && this.addressesForm.get(key).value != null
        && this.addressesForm.get(key).value !== 'undefined'
        && this.addressesForm.get(key).value !== '') {
        duplicateCheckQuery += `&${key}=${this.addressesForm.get(key).value}`;
      }
    });
    duplicateCheckQuery += `&clubId=${this.selectedClub.id}`;

    this.duplicateCheckService.getDuplicates(duplicateCheckQuery, this.selectedClub.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: any) => {
          if (data['hydra:member'].length) {
            this.duplicateCheckInProgress = true;
            this.spinnerService.hide(this.spinnerName);
            this.listOfDuplicates = data['hydra:member'];
          } else {
            this.createCustomer();
          }
        },
        error: (error: Error) => {
          this.spinnerService.hide(this.spinnerName);
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.createCustomerTranslation.i18nErrorCustomerCreate
          });
        }
      });
  }

  /**
   * Go back to previous page.
   */
  public goBack(): void {
    this.router.navigate([`club/${this.selectedClub.id}/search/basic`]);
  }

  /**
   * Go back to previous step.
   */
  public goBackAStep(): void {
    this.stepProcess -= 1;
  }

  /**
   * Go back to previous step.
   */
  public goForwardAStep(): void {
    this.stepProcess += 1;
  }

  /**
   * Checks the radio if it's currently selected then closes it
   */
  public removeRadioValue(value: any, form: UntypedFormGroup): any {
    if (form.value.salutation === value) {
      form.patchValue({salutation: null});
    }
  }

  /**
   * Fill customer form with values.
   */
  public fillCustomerFormPrivate(): void {
    this.customerFormPrivate = this.formBuilder.group({
      firstName: [null, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        noWhitespaceCharacters]],
      lastName: [null, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
        noWhitespaceCharacters]],
      birthday: [null, [isPastDateDirective]],
      salutation: [null],
      company: [null],
      status: 'active',
      title: [null, [Validators.maxLength(20)]],
      club: this.selectedClub['@id'],
      type: CustomerType.PERSON
    });
  }

  /**
   * Fill customer form with values.
   */
  public fillCustomerFormCompany(): void {
    this.customerFormCompany = this.formBuilder.group({
      company: [null, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(150),
        noWhitespaceCharacters
      ]],
      firstName: [null, [
        Validators.minLength(2),
        Validators.maxLength(50),
        noWhitespaceCharacters]],
      lastName: [null, [
        Validators.minLength(2),
        Validators.maxLength(200),
        noWhitespaceCharacters]],
      birthday: [null, [isPastDateDirective]],
      salutation: [null],
      status: 'active',
      title: [null],
      club: this.selectedClub['@id'],
      type: CustomerType.COMPANY
    });
  }

  /**
   * Fill communication form with values.
   */
  public fillCommunicationForm(): void {
    this.communicationForm = this.formBuilder.group({
      email: [null, [
        Validators.maxLength(100),
        isEmailValid,
        noWhitespaceCharacters
      ]],
      phone: [null],
      mobile: [null]
    });

    if (!this.selectedClub.tixxExternalSso) {
      this.communicationForm.addControl('createWithAuthorization', new FormControl(false));
    }
  }

  /**
   * Fill customer form with values.
   */
  public fillFullCustomerForm(customerForm: UntypedFormGroup): void {
    this.fullCustomerForm = this.formBuilder.group({
      firstName: [customerForm.value.firstName],
      lastName: [customerForm.value.lastName],
      birthday: [customerForm.value.birthday],
      salutation: [customerForm.value.salutation],
      company: [customerForm.value.company],
      status: ['active'],
      title: [customerForm.value.title],
      type: [customerForm.value.type],
      club: this.selectedClub['@id'],
      email: [this.communicationForm.value.email],
      phone: [this.communicationForm.value.phone],
      mobile: [this.communicationForm.value.mobile],
    });

    if (!this.selectedClub.tixxExternalSso) {
      this.fullCustomerForm.addControl('createWithAuthorization', new FormControl(this.communicationForm.value.createWithAuthorization));
    }
  }

  /**
   * Fill address form with values.
   */
  public fillAddressForm(): void {
    const defaultCountry = this.selectedClub.country ? this.selectedClub.country : null;
    this.addressesForm = this.formBuilder.group({
      customer: [null],
      street: [null, [Validators.required, isStreetValid, noWhitespaceCharacters]],
      postalCode: [null, [Validators.required, Validators.maxLength(10), noWhitespaceCharacters]],
      city: [null, [Validators.required, Validators.maxLength(40), noWhitespaceCharacters]],
      country: [defaultCountry, [Validators.required, Validators.maxLength(2), Validators.minLength(2), noWhitespaceCharacters]],
      type: 'standard'
    });
    if (defaultCountry === 'DE') {
      this.addressesForm.get('postalCode').setValidators([
        Validators.required, Validators.pattern(/^[0-9]{5}$/), noWhitespaceCharacters
      ]);
    }
    this.addressesForm.get('country').valueChanges.pipe(untilDestroyed(this))
      .subscribe((country: string) => {
        if (country === 'DE') {
          this.addressesForm.get('postalCode').setValidators([
            Validators.required, Validators.pattern(/^[0-9]{5}$/), noWhitespaceCharacters
          ]);
        } else {
          this.addressesForm.get('postalCode').setValidators([
            Validators.required, Validators.maxLength(10), noWhitespaceCharacters
          ]);
        }
        this.addressesForm.get('postalCode').updateValueAndValidity();
      });
  }

  /**
   * Creating the customer is the final step of this process
   */
  public createCustomer(): void {
    this.spinnerService.show(this.spinnerName);
    this.userService.create(this.fullCustomerForm.value)
      .pipe(first())
      .subscribe({
        next: (customer: any) => {
          this.customer = customer;
          this.addressesForm.patchValue({customer: `/api/customers/${this.customer.id}`});
          this.addressService.create(this.addressesForm.value)
            .pipe(first())
            .subscribe({
              next: (address: Address) => {
                this.submitted = true;
                this.notificationService.showNotification({
                  type: NotificationType.SUCCESS,
                  message: this.createCustomerTranslation.i18nSuccessCustomerCreate
                });
                setTimeout(() => {
                  this.spinnerService.hide(this.spinnerName);
                  this.router.navigate(['club', this.selectedClub.id, 'customer', this.customer.id]);
                }, 5000);
              },
              error: (error: Error) => {
                this.spinnerService.hide(this.spinnerName);
                this.notificationService.showNotification({
                  type: NotificationType.ALERT,
                  message: this.createCustomerTranslation.i18nErrorCustomerCreate
                });
              }
            });
        },
        error: (error: Error) => {
          this.spinnerService.hide(this.spinnerName);
          this.submitted = false;
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.createCustomerTranslation.i18nErrorCustomerCreate
          });
        }
      });
  }

  /**
   * Update address values upon Google Places autocomplete address selection event.
   */
  public fillAddressValues(googlePlacesAddress: GooglePlacesAddress): void {
    this.addressesForm.patchValue(googlePlacesAddress);
  }

  /**
   * Checks if the form is filled with data
   */
  public canDeactivate(): Promise<boolean> | boolean {
    if ((this.customerFormPrivate.dirty || this.customerFormCompany.dirty || this.communicationForm.dirty || this.addressesForm.dirty)
      && !this.submitted && this.authenticatedUser && !this.selectedCustomerModal.isOpen) {
      return new Promise<boolean>((resolve, reject) => this.child.showModal(resolve, reject));
    } else {
      return true;
    }
  }

  public openModal() {
    this.selectedCustomerModal.setModalStatus(true);
  }

  public navigateToCustomer() {
    this.router.navigate(['../', 'customer', this.duplicateCheckService.selectedCustomerId], {relativeTo: this.route});
  }

  get authorisationValidity(): boolean {
    const form = this.communicationForm.value;
    return form.createWithAuthorization ? form.email : true;
  }
}
